import { useCallback, useEffect, useMemo, useState } from "react";
import { fetchMemberLevelsForTenant } from "../../../../api/memberLevels";
import { getUserFacingMessage, reportError, showUserNotice } from "../../../../api/errors";
import {
  createMemberMembershipYear,
  fetchMemberMembershipYearLevels,
  fetchMemberMembershipYears,
  migrateMemberMembershipYearsFromLegacy,
  putMemberMembershipYearLevels,
  setCurrentMemberMembershipYear,
  updateMemberMembershipYear,
  type MemberMembershipYearRow,
} from "../../../../api/memberMembershipYears";
import type { MemberLevel } from "../../utils/memberLevelStore";
import { useAdminData } from "../../AdminDataContext";

type LevelDraft = {
  levelKey: string;
  levelName: string;
  annualFeeYuan: number;
  enrollmentBonusPoints: number;
  sortOrder: number;
  isEnabled: boolean;
};

function formatYmd(iso: string): string {
  const s = String(iso || "").trim();
  if (!s) return "—";
  try {
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return s.slice(0, 10);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  } catch {
    return s.slice(0, 10);
  }
}

export default function MemberMembershipYearConfig() {
  const { tenantLegacyId } = useAdminData();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [years, setYears] = useState<MemberMembershipYearRow[]>([]);
  const [templates, setTemplates] = useState<MemberLevel[]>([]);
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [levelDrafts, setLevelDrafts] = useState<LevelDraft[]>([]);
  const [newYearText, setNewYearText] = useState(String(new Date().getFullYear() + 1));

  const selectedRow = useMemo(
    () => years.find((y) => y.year === selectedYear) ?? null,
    [years, selectedYear],
  );

  const loadYears = useCallback(async () => {
    if (!tenantLegacyId.trim()) return;
    setLoading(true);
    setLoadError(null);
    try {
      const [yRows, tpl] = await Promise.all([
        fetchMemberMembershipYears(tenantLegacyId),
        fetchMemberLevelsForTenant(tenantLegacyId),
      ]);
      const sorted = [...yRows].sort((a, b) => b.year - a.year);
      setYears(sorted);
      setTemplates(tpl);
      setSelectedYear((prev) => {
        if (prev != null && sorted.some((x) => x.year === prev)) return prev;
        const cur = sorted.find((x) => x.isCurrent) ?? sorted[0];
        return cur?.year ?? null;
      });
    } catch (e) {
      setLoadError(getUserFacingMessage(e, "加载会员年度失败"));
      reportError(e);
    } finally {
      setLoading(false);
    }
  }, [tenantLegacyId]);

  const loadLevelsForYear = useCallback(
    async (year: number, tpl: MemberLevel[]) => {
      if (!tenantLegacyId.trim()) return;
      try {
        const existing = await fetchMemberMembershipYearLevels(tenantLegacyId, year);
        const byKey = new Map(existing.map((l) => [l.levelKey, l]));
        const drafts: LevelDraft[] = [...tpl]
          .sort((a, b) => a.sortOrder - b.sortOrder)
          .map((t) => {
            const hit = byKey.get(t.id);
            return {
              levelKey: t.id,
              levelName: hit?.levelName ?? t.name,
              annualFeeYuan: hit?.annualFeeYuan ?? 0,
              enrollmentBonusPoints: hit?.enrollmentBonusPoints ?? t.minPoints ?? 0,
              sortOrder: hit?.sortOrder ?? t.sortOrder,
              isEnabled: hit?.isEnabled ?? false,
            };
          });
        setLevelDrafts(drafts);
      } catch (e) {
        reportError(e);
        showUserNotice(getUserFacingMessage(e, "加载年度等级失败"));
      }
    },
    [tenantLegacyId],
  );

  useEffect(() => {
    void loadYears();
  }, [loadYears]);

  useEffect(() => {
    if (selectedYear != null && templates.length > 0) {
      void loadLevelsForYear(selectedYear, templates);
    }
  }, [selectedYear, templates, loadLevelsForYear]);

  const onCreateYear = async () => {
    const yr = parseInt(newYearText.trim(), 10);
    if (!Number.isFinite(yr) || yr < 2000 || yr > 2100) {
      showUserNotice("请填写有效公历年份。");
      return;
    }
    setSaving(true);
    try {
      await createMemberMembershipYear(tenantLegacyId, { year: yr, isEnabled: true });
      showUserNotice("会员年度已创建。");
      await loadYears();
      setSelectedYear(yr);
    } catch (e) {
      reportError(e);
    } finally {
      setSaving(false);
    }
  };

  const onMigrate = async () => {
    if (!window.confirm("将按等级 ValidFrom 年份生成会员年度与年度等级（长期有效档位归入今年）。确定执行？")) {
      return;
    }
    setSaving(true);
    try {
      const r = await migrateMemberMembershipYearsFromLegacy(tenantLegacyId);
      showUserNotice(`已从旧配置迁移 ${r.migratedYearCount} 个年度。`);
      await loadYears();
    } catch (e) {
      reportError(e);
    } finally {
      setSaving(false);
    }
  };

  const onSetCurrent = async (year: number) => {
    setSaving(true);
    try {
      await setCurrentMemberMembershipYear(tenantLegacyId, year);
      showUserNotice("已设为当前年度。");
      await loadYears();
    } catch (e) {
      reportError(e);
    } finally {
      setSaving(false);
    }
  };

  const onToggleYearEnabled = async (row: MemberMembershipYearRow, enabled: boolean) => {
    setSaving(true);
    try {
      await updateMemberMembershipYear(tenantLegacyId, row.year, { isEnabled: enabled });
      await loadYears();
    } catch (e) {
      reportError(e);
    } finally {
      setSaving(false);
    }
  };

  const onSaveLevels = async () => {
    if (selectedYear == null) return;
    const payload = levelDrafts
      .filter((d) => d.isEnabled)
      .map((d) => ({
        levelKey: d.levelKey,
        levelName: d.levelName.trim(),
        annualFeeYuan: Math.max(0, Math.round(d.annualFeeYuan)),
        enrollmentBonusPoints: Math.max(0, Math.round(d.enrollmentBonusPoints)),
        sortOrder: d.sortOrder,
        isEnabled: true,
      }));
    if (payload.length === 0) {
      showUserNotice("请至少启用一个年度等级。");
      return;
    }
    setSaving(true);
    try {
      await putMemberMembershipYearLevels(tenantLegacyId, selectedYear, payload);
      showUserNotice("年度等级已保存。");
      await loadYears();
      await loadLevelsForYear(selectedYear, templates);
    } catch (e) {
      reportError(e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-base font-bold text-gray-800">会员年度</h3>
        <p className="text-xs text-gray-400 mt-1 leading-relaxed">
          按公历年度配置会籍起止、年费与入会/续费赠送积分。LevelKey 须与「会员等级」模板一致；删除年度不可恢复，仅可停用。
        </p>
      </div>

      {loadError ? (
        <div className="rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">{loadError}</div>
      ) : null}

      <div className="rounded-2xl border border-gray-100 bg-white p-5">
        <p className="text-sm font-semibold text-gray-700 mb-3">新建会员年度</p>
        <div className="flex flex-wrap items-center gap-3">
          <input
            type="number"
            className="w-32 rounded-lg border border-gray-200 px-3 py-2 text-sm"
            value={newYearText}
            placeholder="如 2027"
            onChange={(e) => setNewYearText(e.target.value)}
          />
          <button
            type="button"
            disabled={saving}
            onClick={() => void onCreateYear()}
            className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50 cursor-pointer"
          >
            创建
          </button>
          {years.length === 0 ? (
            <button
              type="button"
              disabled={saving}
              onClick={() => void onMigrate()}
              className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm font-medium text-amber-800 hover:bg-amber-100 disabled:opacity-50 cursor-pointer"
            >
              从旧等级字典一键迁移
            </button>
          ) : null}
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-gray-400 text-center py-8">加载中…</p>
      ) : years.length === 0 ? (
        <p className="text-sm text-gray-400 text-center py-8">暂无会员年度，请先创建或迁移。</p>
      ) : (
        <>
          <div className="rounded-2xl border border-gray-100 bg-white overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 text-xs">
                <tr>
                  <th className="text-left px-4 py-3 font-medium">年度</th>
                  <th className="text-left px-4 py-3 font-medium">会籍起止</th>
                  <th className="text-left px-4 py-3 font-medium">等级数</th>
                  <th className="text-left px-4 py-3 font-medium">状态</th>
                  <th className="text-right px-4 py-3 font-medium">操作</th>
                </tr>
              </thead>
              <tbody>
                {years.map((y) => (
                  <tr
                    key={y.year}
                    className={`border-t border-gray-50 cursor-pointer hover:bg-gray-50/80 ${
                      selectedYear === y.year ? "bg-red-50/40" : ""
                    }`}
                    onClick={() => setSelectedYear(y.year)}
                  >
                    <td className="px-4 py-3 font-semibold text-gray-800">
                      {y.year}
                      {y.isCurrent ? (
                        <span className="ml-2 inline-block rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-bold text-red-700">
                          当前
                        </span>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {formatYmd(y.validFrom)} ~ {formatYmd(y.validTo)}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{y.levelCount}</td>
                    <td className="px-4 py-3">
                      <label className="inline-flex items-center gap-2 cursor-pointer" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={y.isEnabled}
                          disabled={saving}
                          onChange={(e) => void onToggleYearEnabled(y, e.target.checked)}
                        />
                        <span className="text-gray-600">{y.isEnabled ? "启用" : "停用"}</span>
                      </label>
                    </td>
                    <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                      {!y.isCurrent ? (
                        <button
                          type="button"
                          disabled={saving}
                          onClick={() => void onSetCurrent(y.year)}
                          className="text-xs font-semibold text-red-600 hover:text-red-700 disabled:opacity-50 cursor-pointer"
                        >
                          设为当前
                        </button>
                      ) : (
                        <span className="text-xs text-gray-300">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {selectedRow && levelDrafts.length > 0 ? (
            <div className="rounded-2xl border border-gray-100 bg-white p-5">
              <p className="text-sm font-semibold text-gray-800 mb-1">{selectedRow.year} 年度等级</p>
              <p className="text-xs text-gray-400 mb-4 leading-relaxed">
                续费入口在存在 Year ≥ 会籍卡上年度+1 且已启用等级时开放；年费与赠送积分仅在此维护。
              </p>
              <div className="space-y-3">
                {levelDrafts.map((d, idx) => (
                  <div key={d.levelKey} className="rounded-xl border border-gray-100 bg-gray-50/60 p-4">
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                      <div>
                        <p className="font-semibold text-gray-800">{d.levelName}</p>
                        <p className="text-xs text-gray-400">{d.levelKey}</p>
                      </div>
                      <label className="inline-flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={d.isEnabled}
                          onChange={(e) => {
                            const on = e.target.checked;
                            setLevelDrafts((prev) => prev.map((x, i) => (i === idx ? { ...x, isEnabled: on } : x)));
                          }}
                        />
                        启用
                      </label>
                    </div>
                    {d.isEnabled ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <label className="block">
                          <span className="text-xs text-gray-500">年费（元）</span>
                          <input
                            type="number"
                            min={0}
                            className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm"
                            value={d.annualFeeYuan}
                            onChange={(e) => {
                              const v = Math.max(0, Math.round(Number(e.target.value) || 0));
                              setLevelDrafts((prev) => prev.map((x, i) => (i === idx ? { ...x, annualFeeYuan: v } : x)));
                            }}
                          />
                        </label>
                        <label className="block">
                          <span className="text-xs text-gray-500">入会/续费赠送积分</span>
                          <input
                            type="number"
                            min={0}
                            className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm"
                            value={d.enrollmentBonusPoints}
                            onChange={(e) => {
                              const v = Math.max(0, Math.round(Number(e.target.value) || 0));
                              setLevelDrafts((prev) =>
                                prev.map((x, i) => (i === idx ? { ...x, enrollmentBonusPoints: v } : x)),
                              );
                            }}
                          />
                        </label>
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
              <button
                type="button"
                disabled={saving}
                onClick={() => void onSaveLevels()}
                className="mt-4 rounded-lg bg-red-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50 cursor-pointer"
              >
                {saving ? "保存中…" : "保存年度等级"}
              </button>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
